import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import FormFileUploadComponents from '@shared/form/form-file-upload/form-file-upload.components';
import FormValidationError from '@shared/form/form-validation-error/form-validation-error';
import Text from '@text';
import FileLabelComponents from '@shared/form/field-label/field-label.components';
import { camelCase } from 'lodash';
import { InputProps } from '../../../../types/form.types';
import { useParamsContext } from '../../../../params/params-context';
import useReasons from '../../../../hooks/use-reasons.hook';

interface Event<T = EventTarget> {
  target: T;
}

const {
  Container,
  Description,
  InputContainer,
  HiddenInput,
  InputZone,
  StyledText
} = FormFileUploadComponents;
const { RequiredMark } = FileLabelComponents;

const FormFileUpload: React.FunctionComponent<InputProps> = ({
  name,
  errorMessage,
  isRequired
}) => {
  const { portalType } = useParamsContext();
  const { register, setValue } = useFormContext();
  const { mainReasonValue, detailReasonValue } = useReasons();
  const [inputText, setInputText] = useState<string>('form.chooseFile');
  const onChangeHandler = useCallback(
    (event: Event<HTMLInputElement>): void => {
      const file = event.target.files;
      setValue(name, file, { shouldValidate: true });
      return file && file[0]
        ? setInputText(file[0].name)
        : setInputText('form.chooseFile');
    },
    [name, setValue]
  );

  const descriptionKey = detailReasonValue
    ? `.${camelCase(mainReasonValue)}.${camelCase(detailReasonValue)}`
    : `.${camelCase(mainReasonValue)}`;

  useEffect(() => {
    return (): void => {
      setValue(name, undefined, { shouldValidate: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return register ? (
    <Container>
      <Description>
        <Text
          tParams={{
            tKey: [
              `form.attachmentDescription.${portalType}${descriptionKey}`,
              'form.attachmentDescription.unspecific'
            ]
          }}
        />
      </Description>
      <InputContainer>
        <InputZone>
          <StyledText tParams={inputText} />
          {isRequired && <RequiredMark>*</RequiredMark>}
          <HiddenInput
            {...register(name)}
            data-test-id={name}
            type="file"
            onChange={onChangeHandler}
          />
        </InputZone>
        <FormValidationError errorMessage={errorMessage} />
      </InputContainer>
    </Container>
  ) : null;
};

export default FormFileUpload;
